import { Col, DatePicker, Input, Row, Select, Spin, Table, Tabs } from "antd";
import { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentBox } from "../../../components/layout/content";
import { LoginedLayout } from "../../../components/layout/loginLayout";
import { color } from "../../../res/color";
import { GET } from "../../../services";
import { CHECKER_NEW_SUM_ORDER_BY_DATE, CHECKER_NEW_SUM_SKU_BY_DATE } from "../../../services/path";
import "./style.css";

interface RowData {
  brand: string;
  platform: string;
  actualBrand: string;
  actualPlatform: string;
  logistic: string;
  batch_generated: string;
  waiting: number;
  ready: number;
  confirm: number;
  pick: number;
  packed: number;
  checked_out: number;
  shipping: number;
  complete: number;
  cancel: number;
  cancel_ship: number;
  hold: number;
  return: number;
}
interface SkuData {
  sku: string;
  ready: number;
  confirm: number;
  pick: number;
  packed: number;
  checked_out: number;
  shipping: number;
  complete: number;
  waiting: number;
}

const SumOrder = () => {
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [loading, setLoading] = useState(false);
  const [dataOrder, setDataOrder] = useState<RowData[]>([]);
  const [dataSku, setDataSku] = useState<SkuData[]>([]);
  const [filteredData, setFilteredData] = useState(dataSku);
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const { TabPane } = Tabs;
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const pageOptions = ["10", "25", "50", "100"];
  const handleSearch = (value: string) => {
    setSearchValue(value.trim());
  };

  function formatNumber(number: number) {
    return number.toLocaleString();
  }

  useEffect(() => {
    const trimmedSearchValue = searchValue || "";
    if (!trimmedSearchValue) {
      setFilteredData(dataSku);
    } else {
      const filteredList = dataSku.filter((item) =>
        item.sku.toUpperCase().includes(searchValue.toUpperCase()),
      );

      setFilteredData(filteredList);
    }
  }, [searchValue, dataSku]);

  const fetchOrderData = async () => {
    try {
      setLoading(true);
      const searchOrder = CHECKER_NEW_SUM_ORDER_BY_DATE(startDate);
      const result = await GET(searchOrder);
      if (result !== null && result.result !== null) {
        setDataOrder(result.result);
      } else {
        setDataOrder([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSkuData = async () => {
    try {
      setLoading(true);
      const searchSku = CHECKER_NEW_SUM_SKU_BY_DATE(startDate);
      const result = await GET(searchSku);
      if (result !== null && result.result !== null) {
        setDataSku(result.result);
      } else {
        setDataSku([]);
      }
    } catch (error) {
      console.error("Error fetching SKU data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderData();
    fetchSkuData();
  }, [startDate]);

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current;
      const isOverflowing = table.scrollWidth > table.clientWidth;
      table.style.overflowX = isOverflowing ? "scroll" : "auto";
    }
  }, [dataOrder]);

  const navigate = useNavigate();
  const handleRowClick = (record: any, columnName: string) => {
    if (startDate) {
      const { actualBrand, actualPlatform, logistic } = record;
      const formattedDate = startDate.format("YYYY-MM-DD");
      localStorage.setItem("date", formattedDate);
      localStorage.setItem("brand", actualBrand);
      localStorage.setItem("logistic", logistic);
      localStorage.setItem("platform", actualPlatform);
      localStorage.setItem("isExpressDelivery", "false");
      if (columnName !== "batch_generated") {
        localStorage.setItem("operationStatus", columnName);
      }

      navigate(`/search/order`);
    }
  };

  dataOrder.sort((a, b) => {
    const brandComparison = a.brand.localeCompare(b.brand);
    if (brandComparison !== 0) {
      return brandComparison;
    }

    return a.platform.localeCompare(b.platform);
  });

  const brandRowSpans: Record<string, number> = {};
  const platformRowSpans: Record<string, number> = {};
  dataOrder.forEach((row) => {
    brandRowSpans[row.brand] = (brandRowSpans[row.brand] || 0) + 1;
    const key = `${row.brand}-${row.platform}`;
    platformRowSpans[key] = (platformRowSpans[key] || 0) + 1;
  });

  const mergedData: RowData[] = [];
  dataOrder.forEach((row, index) => {
    if (index === 0 || row.brand !== dataOrder[index - 1].brand) {
      // Add a new row for the brand
      mergedData.push({
        brand: row.brand,
        platform: row.platform,
        actualBrand: row.brand, // ฟิลด์เพิ่มเติม
        actualPlatform: row.platform, // ฟิลด์เพิ่มเติม
        logistic: row.logistic,
        batch_generated: row.batch_generated,
        waiting: row.waiting,
        ready: row.ready,
        confirm: row.confirm,
        pick: row.pick,
        packed: row.packed,
        checked_out: row.checked_out,
        shipping: row.shipping,
        complete: row.complete,
        return: row.return,
        cancel: row.cancel,
        cancel_ship: row.cancel_ship,
        hold: row.hold,
      });
    } else if (
      row.platform !== dataOrder[index - 1].platform ||
      row.brand !== dataOrder[index - 1].brand
    ) {
      // Add a new row for the platform within the same brand or different brand
      mergedData.push({
        brand: row.brand === dataOrder[index - 1].brand ? "" : row.brand,
        platform: row.platform,
        actualBrand: row.brand, // ฟิลด์เพิ่มเติม
        actualPlatform: row.platform, // ฟิลด์เพิ่มเติม
        logistic: row.logistic,
        batch_generated: row.batch_generated,
        waiting: row.waiting,
        ready: row.ready,
        confirm: row.confirm,
        pick: row.pick,
        packed: row.packed,
        checked_out: row.checked_out,
        shipping: row.shipping,
        complete: row.complete,
        return: row.return,
        cancel: row.cancel,
        cancel_ship: row.cancel_ship,
        hold: row.hold,
      });
    } else {
      // Add a row with empty brand and platform
      mergedData.push({
        brand: "",
        platform: "",
        actualBrand: row.brand, // ฟิลด์เพิ่มเติม
        actualPlatform: row.platform, // ฟิลด์เพิ่มเติม
        logistic: row.logistic,
        batch_generated: row.batch_generated,
        waiting: row.waiting,
        ready: row.ready,
        confirm: row.confirm,
        pick: row.pick,
        packed: row.packed,
        checked_out: row.checked_out,
        shipping: row.shipping,
        complete: row.complete,
        return: row.return,
        cancel: row.cancel,
        cancel_ship: row.cancel_ship,
        hold: row.hold,
      });
    }
  });

  const calculateSummary = (data: any[]) => {
    return data.reduce(
      (acc, record) => {
        acc.batch_generated += record.batch_generated || 0;
        acc.waiting += record.waiting || 0;
        acc.ready += record.ready || 0;
        acc.confirm += record.confirm || 0;
        acc.pick += record.pick || 0;
        acc.packed += record.packed || 0;
        acc.checked_out += record.checked_out || 0;
        acc.shipping += record.shipping || 0;
        acc.complete += record.complete || 0;
        acc.cancel += record.cancel || 0;
        acc.cancel_ship += record.cancel_ship || 0;
        acc.hold += record.hold || 0;
        acc.return += record.return || 0;
        return acc;
      },
      {
        batch_generated: 0,
        waiting: 0,
        ready: 0,
        confirm: 0,
        pick: 0,
        packed: 0,
        checked_out: 0,
        shipping: 0,
        complete: 0,
        cancel: 0,
        cancel_ship: 0,
        hold: 0,
        return: 0,
      },
    );
  };

  const totalSummary = calculateSummary(mergedData);
  const dynamicColumns: ColumnType<RowData>[] = [
    {
      title: <div style={{ textAlign: "center" }}>Brand</div>,
      dataIndex: "brand",
      key: "brand",
      render: (text: string, record: RowData) => {
        if (brandRowSpans[record.brand]) {
          return {
            children: <strong>{text}</strong>,
            props: {
              rowSpan: brandRowSpans[record.brand],
            },
          };
        }
        return {
          children: <strong>{text}</strong>,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Platform</div>,
      dataIndex: "platform",
      key: "platform",
      render: (text: string, record: RowData) => {
        const platformKey = `${record.actualBrand}-${record.platform}`;
        if (platformRowSpans[platformKey] && record.platform) {
          return {
            children: <strong>{text}</strong>,
            props: {
              rowSpan: platformRowSpans[platformKey],
            },
          };
        }
        return {
          children: <strong>{text}</strong>,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Logistic</div>,
      dataIndex: "logistic",
      key: "logistic",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Gen Batch</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.batch_generated)})
          </p>
        </div>
      ),
      dataIndex: "batch_generated",
      key: "batch_generated",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "batch_generated"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Waiting</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.waiting)})
          </p>
        </div>
      ),
      dataIndex: "waiting",
      key: "waiting",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "waiting"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Ready</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.ready)})
          </p>
        </div>
      ),
      dataIndex: "ready",
      key: "ready",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "ready"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Confirm</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.confirm)})
          </p>
        </div>
      ),
      dataIndex: "confirm",
      key: "confirm",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "confirm"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Picked</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>({formatNumber(totalSummary.pick)})</p>
        </div>
      ),
      dataIndex: "pick",
      key: "pick",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "picked"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Packed</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.packed)})
          </p>
        </div>
      ),
      dataIndex: "packed",
      key: "packed",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "packed"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Checked Out</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.checked_out)})
          </p>
        </div>
      ),
      dataIndex: "checked_out",
      key: "checked_out",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "checked out"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Shipping</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.shipping)})
          </p>
        </div>
      ),
      dataIndex: "shipping",
      key: "shipping",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "shipping"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Complete</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.complete)})
          </p>
        </div>
      ),
      dataIndex: "complete",
      key: "complete",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "complete"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Cancel</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.cancel)})
          </p>
        </div>
      ),
      dataIndex: "cancel",
      key: "cancel",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "cancel"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Cancel Shipping</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.cancel_ship)})
          </p>
        </div>
      ),
      dataIndex: "cancel_ship",
      key: "cancel_ship",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "cancel-ship"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Hold</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>({formatNumber(totalSummary.hold)})</p>
        </div>
      ),
      dataIndex: "hold",
      key: "hold",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "hold"),
      }),
      align: "center",
    },
    {
      title: (
        <div style={{ textAlign: "center" }}>
          <p>Return</p>
          <p style={{ color: color.THEME, fontWeight: 800 }}>
            ({formatNumber(totalSummary.return)})
          </p>
        </div>
      ),
      dataIndex: "return",
      key: "return",
      onCell: (record: any) => ({
        onDoubleClick: () => handleRowClick(record, "return"),
      }),
      align: "center",
    },
  ];

  const skuColumns = [
    {
      title: <div style={{ textAlign: "center" }}>SKU ID</div>,
      dataIndex: "sku",
      key: "sku",
      sorter: (a: SkuData, b: SkuData) => a.sku.localeCompare(b.sku),
    },
    {
      title: <div style={{ textAlign: "center" }}>Waiting</div>,
      dataIndex: "waiting",
      key: "waiting",
      sorter: (a: SkuData, b: SkuData) => a.waiting - b.waiting,
    },
    {
      title: <div style={{ textAlign: "center" }}>Ready</div>,
      dataIndex: "ready",
      key: "ready",
      sorter: (a: SkuData, b: SkuData) => a.ready - b.ready,
    },
    {
      title: <div style={{ textAlign: "center" }}>Confirm</div>,
      dataIndex: "confirm",
      key: "confirm",
      sorter: (a: SkuData, b: SkuData) => a.confirm - b.confirm,
    },
    {
      title: <div style={{ textAlign: "center" }}>Picked</div>,
      dataIndex: "pick",
      key: "pick",
      sorter: (a: SkuData, b: SkuData) => a.pick - b.pick,
    },
    {
      title: <div style={{ textAlign: "center" }}>Packed</div>,
      dataIndex: "packed",
      key: "packed",
      sorter: (a: SkuData, b: SkuData) => a.packed - b.packed,
    },
    {
      title: <div style={{ textAlign: "center" }}>Checked Out</div>,
      dataIndex: "checked_out",
      key: "checked_out",
      sorter: (a: SkuData, b: SkuData) => a.checked_out - b.checked_out,
    },
    {
      title: <div style={{ textAlign: "center" }}>Shipping</div>,
      dataIndex: "shipping",
      key: "shipping",
      sorter: (a: SkuData, b: SkuData) => a.shipping - b.shipping,
    },
    {
      title: <div style={{ textAlign: "center" }}>Complete</div>,
      dataIndex: "complete",
      key: "complete",
      sorter: (a: SkuData, b: SkuData) => a.complete - b.complete,
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox>
        {loading && <Spin size="large" style={{ position: "absolute", top: "50%", left: "50%" }} />}
        <div
          style={{
            marginBottom: "16px",
            borderBottom: "1px solid #e8e8e8",
            paddingBottom: "8px",
          }}
        >
          <h2 style={{ marginBottom: "8px" }}>Daily Order</h2>
        </div>
        <span>Date Range:</span>
        <DatePicker
          className="small-date-picker"
          style={{ marginLeft: "8px", marginBottom: "20px" }}
          showTime={false}
          defaultValue={dayjs()}
          onChange={(dates) => {
            if (dates) {
              setStartDate(dates);
            } else {
              setStartDate(null);
            }
          }}
        />
        <Row gutter={[16, 16]} style={{ marginBottom: "15px" }}>
          <Col xs={14} sm={12} md={14} lg={18} xl={18}>
            <span>Show: </span>
            <Select
              value={pageSize.toString()}
              onChange={(value) => handlePageSizeChange(parseInt(value))}
            >
              {pageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </Col>
          <Col xs={10} sm={12} md={10} lg={6} xl={6}>
            <Input.Search
              placeholder="Enter SKU ID"
              onSearch={handleSearch}
              value={searchValue} // Set the value of the search input
              onChange={(e) => setSearchValue(e.target.value)} // Update searchValue on input change
              className={`fade-in-out ${activeTab === "1" ? "" : "visible"}`}
            />
          </Col>
        </Row>
        <Tabs
          defaultActiveKey="1"
          type="card"
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
        >
          <TabPane tab="Order" key="1">
            <div ref={tableRef}>
              <Table
                columns={dynamicColumns}
                dataSource={mergedData}
                pagination={false}
                scroll={{ x: 1500 }}
                onRow={(record) => ({
                  onDoubleClick: (event) => {
                    const clickedColumn = event.currentTarget.getAttribute("data-column");
                    const clickableColumns = [
                      "batch_generated",
                      "waiting",
                      "ready",
                      "picked",
                      "packed",
                      "checked out",
                      "shipping",
                      "completed",
                    ];
                    if (clickableColumns.includes(clickedColumn)) {
                      handleRowClick(record, clickedColumn);
                    }
                  },
                })}
                summary={() => {
                  return (
                    <Table.Summary.Row className="custom-summary-row">
                      <Table.Summary.Cell index={0} colSpan={3}>
                        <strong>Total</strong>
                      </Table.Summary.Cell>
                      {Object.keys(totalSummary).map((key, index) => (
                        <Table.Summary.Cell index={index + 1} align="center">
                          <strong>{formatNumber(totalSummary[key])}</strong>
                        </Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                  );
                }}
              />
            </div>
          </TabPane>
          <TabPane tab="SKU" key="2">
            <div ref={tableRef}>
              <Table
                columns={skuColumns}
                dataSource={filteredData}
                pagination={{
                  pageSize: pageSize, // Set the number of rows per page to 25
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onShowSizeChange: (current, size) => handlePageSizeChange(size),
                  current: currentPage,
                  onChange: (page) => setCurrentPage(page),
                  total: filteredData.length,
                }}
                scroll={{ x: 1500 }}
              />
            </div>
          </TabPane>
        </Tabs>
      </ContentBox>
    </LoginedLayout>
  );
};

export default SumOrder;
